// src/shared/components/ui/Form/Form.tsx

import { type ReactNode, type FormHTMLAttributes } from "react";
import {
  useForm,
  FormProvider,
  type UseFormProps,
  type FieldValues,
  type SubmitHandler,
  type UseFormReturn,
} from "react-hook-form";

/** Types */
interface FormPropsBase<T extends FieldValues> extends Omit<
  FormHTMLAttributes<HTMLFormElement>,
  "onSubmit"
> {
  children?: ReactNode;
  onSubmit: SubmitHandler<T>;
  className?: string;
}

// When using internal form (Form creates its own instance)
interface FormPropsWithOptions<T extends FieldValues> extends FormPropsBase<T> {
  options?: UseFormProps<T>;
  form?: never;
  render?: (methods: UseFormReturn<T>) => ReactNode;
}

// When using external form (you provide the form instance)
interface FormPropsWithForm<T extends FieldValues> extends FormPropsBase<T> {
  form: UseFormReturn<T>;
  options?: never;
  render?: (methods: UseFormReturn<T>) => ReactNode;
}

type FormProps<T extends FieldValues> =
  | FormPropsWithOptions<T>
  | FormPropsWithForm<T>;

/**
 * Form - Wrapper component that provides React Hook Form context
 *
 * @example Using internal form (Form creates instance)
 * ```tsx
 * <Form
 *   onSubmit={(data) => console.log(data)}
 *   options={{ defaultValues: { email: "" } }}
 * >
 *   <FormInput name="email" label="Email" />
 * </Form>
 * ```
 *
 * @example Using external form (you provide instance)
 * ```tsx
 * const { form, onSubmit } = useUserForm({ user, onClose });
 *
 * <Form form={form} onSubmit={onSubmit}>
 *   <FormInput name="email" label="Email" />
 * </Form>
 * ```
 */
export function Form<T extends FieldValues>({
  children,
  onSubmit,
  options,
  form: externalForm,
  render,
  className,
  ...formProps
}: FormProps<T>) {
  // Use external form if provided, otherwise create internal form
  const internalForm = useForm<T>(options);
  const form = externalForm || internalForm;

  return (
    <FormProvider {...form}>
      <form
        className={className}
        onSubmit={form.handleSubmit(onSubmit)}
        noValidate
        {...formProps}
      >
        {render ? render(form) : children}
      </form>
    </FormProvider>
  );
}

Form.displayName = "Form";
